/**
 * Collection health (plan §18): feed freshness per tracked instrument,
 * coverage gaps (never filled with fabricated data), tracked instruments and
 * archive verification status.
 */
import { Badge, Group, Paper, SimpleGrid, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";

import { upgradeApi } from "../api/fleet";

function age(ts: unknown): string {
  if (!ts) return "never";
  const ms = Date.now() - new Date(String(ts)).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  return `${Math.round(ms / 3_600_000)}h ago`;
}

export function CollectionHealthPage() {
  const q = useQuery({ queryKey: ["catalog", "coverage"], queryFn: () => upgradeApi.catalog.coverage(), refetchInterval: 15_000 });
  const d = q.data;
  if (!d) return <Text c="dimmed">Loading…</Text>;
  const openGaps = d.gaps.filter((g) => !g.ended_at);
  return (
    <>
      <Title order={2} mb="lg">Collection Health</Title>
      <SimpleGrid cols={{ base: 2, md: 4 }} mb="lg">
        <Paper withBorder p="sm"><Text size="xs" c="dimmed">Active subscriptions</Text><Text fw={700}>{d.subscriptions.filter((s) => s.status === "active").length}</Text></Paper>
        <Paper withBorder p="sm"><Text size="xs" c="dimmed">Tracked instruments</Text><Text fw={700}>{d.instruments.length}</Text></Paper>
        <Paper withBorder p="sm"><Text size="xs" c="dimmed">Open coverage gaps</Text><Group gap="xs"><Text fw={700}>{openGaps.length}</Text>{openGaps.length > 0 && <Badge color="red" size="xs">attention</Badge>}</Group></Paper>
        <Paper withBorder p="sm"><Text size="xs" c="dimmed">Archive segments verified / pending</Text><Text fw={700}>{String(d.archive?.verified ?? 0)} / {String(d.archive?.pending ?? 0)}</Text></Paper>
      </SimpleGrid>

      <Title order={4} mb="xs">Subscriptions</Title>
      <Paper withBorder p="xs" mb="lg">
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Label</Table.Th><Table.Th>Kind</Table.Th><Table.Th>Status</Table.Th><Table.Th>Last discovery</Table.Th><Table.Th>Tracked</Table.Th><Table.Th>Retention</Table.Th><Table.Th>Last error</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>{d.subscriptions.map((s) => {
            const h = (s.health ?? {}) as Record<string, unknown>;
            return (
              <Table.Tr key={String(s.id)}>
                <Table.Td>{String(s.label)}</Table.Td><Table.Td>{String(s.kind)}</Table.Td>
                <Table.Td><Badge color={s.status === "active" ? "green" : "gray"}>{String(s.status)}</Badge></Table.Td>
                <Table.Td>{s.kind === "crypto_product" && !h.last_discovery_at
                  ? <Text size="xs" c="dimmed">n/a — collected by the market data loop</Text>
                  : age(h.last_discovery_at)}</Table.Td><Table.Td>{String(h.instruments_tracked ?? "—")}</Table.Td>
                <Table.Td>{String(s.retention_class)}</Table.Td><Table.Td><Text size="xs" c="red">{h.last_error ? String(h.last_error) : ""}</Text></Table.Td>
              </Table.Tr>
            );
          })}</Table.Tbody>
        </Table>
      </Paper>

      <Title order={4} mb="xs">Tracked instruments (feed freshness)</Title>
      <Paper withBorder p="xs" mb="lg">
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Event</Table.Th><Table.Th>Lifecycle</Table.Th><Table.Th>Status</Table.Th><Table.Th>Legacy market</Table.Th><Table.Th>Last quote</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>{d.instruments.map((i) => (
            <Table.Tr key={String(i.instrument_id)}>
              <Table.Td>{String(i.title)}</Table.Td><Table.Td><Badge variant="light">{String(i.lifecycle)}</Badge></Table.Td>
              <Table.Td>{String(i.status)}</Table.Td><Table.Td>{i.legacy_market_id ? String(i.legacy_market_id) : "—"}</Table.Td>
              <Table.Td>{i.legacy_market_id ? <Text size="xs" c="dimmed">crypto ticks (window_ticks_1s)</Text> : <Badge color={i.last_quote_at && Date.now() - new Date(String(i.last_quote_at)).getTime() < 120_000 ? "green" : "yellow"}>{age(i.last_quote_at)}</Badge>}</Table.Td>
            </Table.Tr>
          ))}</Table.Tbody>
        </Table>
      </Paper>

      <Title order={4} mb="xs">Coverage gaps (recorded, never back-filled)</Title>
      <Paper withBorder p="xs">
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Kind</Table.Th><Table.Th>Scope</Table.Th><Table.Th>Started</Table.Th><Table.Th>Ended</Table.Th><Table.Th>Detail</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>{d.gaps.map((g) => (
            <Table.Tr key={String(g.id)}>
              <Table.Td><Badge color={g.ended_at ? "gray" : "red"}>{String(g.kind)}</Badge></Table.Td>
              <Table.Td><Text size="xs">{String(g.instrument_id ?? g.legacy_market_id ?? "collector")}</Text></Table.Td>
              <Table.Td>{new Date(String(g.started_at)).toLocaleString()}</Table.Td>
              <Table.Td>{g.ended_at ? new Date(String(g.ended_at)).toLocaleString() : <Text c="red" span>open</Text>}</Table.Td>
              <Table.Td><Text size="xs" c="dimmed">{JSON.stringify(g.detail)}</Text></Table.Td>
            </Table.Tr>
          ))}</Table.Tbody>
        </Table>
      </Paper>
    </>
  );
}
